import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, MessageCircle, Mail, CalendarDays, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHero, Reveal, InfoCards, useSettings, DEFAULT_CONTACT, WHATSAPP_SERVICES } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MOTIFS = [
  "Réservation",
  "Commande",
  "Demande de devis",
  "Recrutement",
  "Partenariat",
  "Information",
  "Autre",
];

const ACTIVITY_PARAMS = {
  services: "Maasaï Services",
  comptoir: "Le Comptoir Maasaï",
  palais: "Palais Maasaï",
};

const initialForm = { name: "", phone: "", email: "", activity: "", subject: "", message: "" };

export default function Contact() {
  const settings = useSettings();
  const contact = { ...DEFAULT_CONTACT, ...(settings?.contact || {}) };
  const [params] = useSearchParams();
  const presetActivity = ACTIVITY_PARAMS[params.get("activity")] || "";
  const [form, setForm] = useState({ ...initialForm, activity: presetActivity });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.activity) {
      toast.error("Merci de choisir l'activité concernée.");
      return;
    }
    if (!form.subject) {
      toast.error("Merci de choisir le motif de votre demande.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email || "non-renseigne@site.fr",
        activity: form.activity,
        subject: form.subject,
        message: `Téléphone : ${form.phone || "—"}\n\n${form.message}`,
      });
      setSent(true);
      setForm({ ...initialForm, activity: presetActivity });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero overline="Contact" title="Écrivez-nous" testId="contact-hero" />

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <InfoCards />
          </Reveal>

          <Reveal delay={0.1} className="mt-10">
            <div className="flex flex-col gap-4 sm:flex-row" data-testid="contact-actions">
              <Link to="/reservation" className="btn-primary flex-1" data-testid="contact-reserve-button">
                <CalendarDays className="h-4 w-4" strokeWidth={1.5} />
                Réserver en ligne
              </Link>
              <a
                href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Bonjour, je souhaite contacter l'Univers Maasaï.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold flex-1"
                data-testid="contact-whatsapp-button"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                WhatsApp Business
              </a>
              <a href={`mailto:${contact.email}`} className="btn-outline flex-1" data-testid="contact-email-button">
                <Mail className="h-4 w-4" strokeWidth={1.5} />
                Envoyer un email
              </a>
            </div>
          </Reveal>

          <div className="mt-24 grid gap-16 lg:grid-cols-2">
            <Reveal>
              <h2 className="text-2xl font-semibold tracking-tight text-ivory">
                Formulaire de contact
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-ivory/50">
                Une seule adresse pour tout l'Univers Maasaï : indiquez l'activité concernée et le
                motif de votre demande — elle sera automatiquement classée et transmise à la bonne
                équipe. Nous vous répondons dans les meilleurs délais.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              {sent ? (
                <div className="rounded-lg border border-white/10 bg-coal/60 py-16 text-center" data-testid="contact-success">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-6 text-lg font-semibold text-ivory">Message envoyé !</h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ivory/55">
                    Merci ! Votre demande a bien été transmise à l'équipe concernée. Nous revenons
                    vers vous rapidement.
                  </p>
                  <button onClick={() => setSent(false)} className="btn-outline mt-8" data-testid="contact-new-message-button">
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                        Quelle activité souhaitez-vous contacter ?
                      </label>
                      <Select value={form.activity} onValueChange={(v) => setForm((f) => ({ ...f, activity: v }))}>
                        <SelectTrigger className="!h-12 border-white/10 bg-white/5 text-sm text-ivory" data-testid="contact-activity-select">
                          <SelectValue placeholder="Choisir une activité" />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-coal text-ivory">
                          {WHATSAPP_SERVICES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                        Motif de votre demande
                      </label>
                      <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
                        <SelectTrigger className="!h-12 border-white/10 bg-white/5 text-sm text-ivory" data-testid="contact-subject-select">
                          <SelectValue placeholder="Choisir un motif" />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-coal text-ivory">
                          {MOTIFS.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Nom et prénom"
                    className="input-dark"
                    data-testid="contact-name-input"
                  />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Téléphone (optionnel)"
                      className="input-dark"
                      data-testid="contact-phone-input"
                    />
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="Email (optionnel)"
                      className="input-dark"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <textarea
                    required
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Votre message"
                    rows={6}
                    className="input-dark !h-auto py-4"
                    data-testid="contact-message-input"
                  />
                  <button type="submit" disabled={sending} className="btn-primary" data-testid="contact-submit-button">
                    <Send className="h-4 w-4" strokeWidth={1.5} />
                    {sending ? "Envoi…" : "Envoyer ma demande"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
